export interface LayoutSample {
  top: number;
  left: number;
  width: number;
  height: number;
  scrollY: number;
}

type LayoutPhase = 'idle' | 'sampling' | 'settle-frame' | 'delivering';
type WatchdogHandle = ReturnType<typeof setTimeout>;

interface LayoutStabilitySchedulerOptions {
  sample: () => LayoutSample;
  deliver: () => void;
  isActive: () => boolean;
  requestFrame: (callback: FrameRequestCallback) => number;
  cancelFrame: (id: number) => void;
  setWatchdog: (callback: () => void, timeout: number) => WatchdogHandle;
  clearWatchdog: (id: WatchdogHandle) => void;
  now: () => number;
  warn: (message: string, error?: unknown) => void;
}

const REQUIRED_STABLE_FRAMES = 5;
const POSITION_TOLERANCE = 1;
const MAX_WAIT_TIME = 10000;

function isStable(current: LayoutSample, previous: LayoutSample) {
  return Math.abs(current.top - previous.top) <= POSITION_TOLERANCE
    && Math.abs(current.left - previous.left) <= POSITION_TOLERANCE
    && Math.abs(current.width - previous.width) <= POSITION_TOLERANCE
    && Math.abs(current.height - previous.height) <= POSITION_TOLERANCE
    && Math.abs(current.scrollY - previous.scrollY) <= POSITION_TOLERANCE;
}

function sampleSignature(sample: LayoutSample) {
  return [
    sample.top,
    sample.left,
    sample.width,
    sample.height,
    sample.scrollY,
  ].join(':');
}

export class LayoutStabilityScheduler {
  private options: LayoutStabilitySchedulerOptions;
  private phase: LayoutPhase = 'idle';
  private frameId: number | null = null;
  private watchdogId: WatchdogHandle | null = null;
  private generation = 0;
  private burstId = 0;
  private revision = 0;
  private sampledRevision = 0;
  private startedAt = 0;
  private stableFrames = 0;
  private lastSample: LayoutSample | null = null;
  private rerunAfterDelivery = false;
  private suppressedSignature: string | null = null;

  constructor(options: LayoutStabilitySchedulerOptions) {
    this.options = options;
  }

  setDeliver(deliver: () => void) {
    this.options.deliver = deliver;
  }

  notify() {
    if (!this.options.isActive()) return;
    this.revision += 1;

    if (this.phase === 'delivering') {
      this.rerunAfterDelivery = true;
      return;
    }
    if (this.phase === 'idle') {
      this.startBurst(null);
    }
  }

  stop() {
    this.generation += 1;
    this.cancelScheduledWork();
    this.phase = 'idle';
    this.revision = 0;
    this.sampledRevision = 0;
    this.stableFrames = 0;
    this.lastSample = null;
    this.rerunAfterDelivery = false;
    this.suppressedSignature = null;
  }

  private startBurst(suppressedSignature: string | null) {
    this.burstId += 1;
    this.phase = 'sampling';
    this.startedAt = this.options.now();
    this.sampledRevision = this.revision;
    this.stableFrames = 0;
    this.lastSample = null;
    this.rerunAfterDelivery = false;
    this.suppressedSignature = suppressedSignature;
    const generation = this.generation;
    const burstId = this.burstId;
    let ownWatchdogId: WatchdogHandle;
    ownWatchdogId = this.options.setWatchdog(() => {
      if (generation !== this.generation
          || burstId !== this.burstId
          || this.watchdogId !== ownWatchdogId
          || !this.options.isActive()) return;
      this.watchdogId = null;
      this.options.warn('DOM stability wait timeout');
      this.finish(this.options.sample(), burstId);
    }, MAX_WAIT_TIME);
    this.watchdogId = ownWatchdogId;
    this.scheduleFrame();
  }

  private scheduleFrame() {
    if (this.frameId !== null) return;
    const generation = this.generation;
    const burstId = this.burstId;
    let ownFrameId = 0;
    ownFrameId = this.options.requestFrame((timestamp) => {
      if (generation !== this.generation
          || burstId !== this.burstId
          || this.frameId !== ownFrameId
          || !this.options.isActive()) return;
      this.frameId = null;
      this.checkFrame(timestamp, burstId);
    });
    this.frameId = ownFrameId;
  }

  private checkFrame(timestamp: number, burstId: number) {
    if (timestamp - this.startedAt >= MAX_WAIT_TIME) {
      this.options.warn('DOM stability wait timeout');
      this.finish(this.options.sample(), burstId);
      return;
    }

    if (this.revision !== this.sampledRevision) {
      this.sampledRevision = this.revision;
      this.phase = 'sampling';
      this.stableFrames = 0;
      this.lastSample = null;
    }

    const currentSample = this.options.sample();
    if (this.phase === 'settle-frame') {
      if (this.lastSample && isStable(currentSample, this.lastSample)) {
        this.finish(currentSample, burstId);
        return;
      }
      this.phase = 'sampling';
      this.stableFrames = 0;
      this.lastSample = currentSample;
      this.scheduleFrame();
      return;
    }

    if (this.lastSample && isStable(currentSample, this.lastSample)) {
      this.stableFrames += 1;
      if (this.stableFrames >= REQUIRED_STABLE_FRAMES) {
        this.phase = 'settle-frame';
      }
    } else {
      this.stableFrames = 0;
    }
    this.lastSample = currentSample;
    this.scheduleFrame();
  }

  private finish(finalSample: LayoutSample, burstId: number) {
    if (burstId !== this.burstId || this.phase === 'idle') return;
    this.cancelScheduledWork();
    const signature = sampleSignature(finalSample);
    const shouldDeliver = signature !== this.suppressedSignature;
    const revisionBeforeDelivery = this.revision;
    this.phase = 'delivering';

    try {
      if (shouldDeliver && this.options.isActive()) {
        this.options.deliver();
      }
    } catch (error) {
      this.options.warn('View onLayout callback failed', error);
    } finally {
      const shouldRerun = this.rerunAfterDelivery
        || this.revision !== revisionBeforeDelivery;
      this.phase = 'idle';
      this.stableFrames = 0;
      this.lastSample = null;
      this.rerunAfterDelivery = false;
      this.suppressedSignature = null;
      if (shouldRerun && this.options.isActive()) {
        // Suppress only an unchanged, immediate callback self-feedback burst.
        // A later independent notification keeps the legacy delivery contract.
        this.startBurst(signature);
      }
    }
  }

  private cancelScheduledWork() {
    if (this.frameId !== null) {
      this.options.cancelFrame(this.frameId);
      this.frameId = null;
    }
    if (this.watchdogId !== null) {
      this.options.clearWatchdog(this.watchdogId);
      this.watchdogId = null;
    }
  }
}
